import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Box,
  Chip
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { useEditMode } from '../context/EditModeContext';

// En producción, usar URL relativa (Nginx redirige /api/ al backend)
// En desarrollo, usar localhost:5000
// Si REACT_APP_API_URL está configurado pero es HTTP, usar ruta relativa en HTTPS
let API_URL = process.env.REACT_APP_API_URL || '';
if (API_URL && API_URL.startsWith('http://') && typeof window !== 'undefined' && window.location.protocol === 'https:') {
  // Si estamos en HTTPS pero API_URL es HTTP, usar ruta relativa
  API_URL = '';
} else if (!API_URL && process.env.NODE_ENV !== 'production') {
  API_URL = 'http://localhost:5000';
}

function PropiedadesList() {
  const [propiedades, setPropiedades] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [nombrePropiedad, setNombrePropiedad] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const { editMode } = useEditMode();
  const navigate = useNavigate();

  useEffect(() => {
    cargarPropiedades();
  }, []);

  const cargarPropiedades = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/propiedades`);
      setPropiedades(response.data);
    } catch (error) {
      console.error('Error cargando propiedades:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAgregar = async (e) => {
    e.preventDefault();
    
    if (!nombrePropiedad.trim()) {
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/api/propiedades`, {
        nombre: nombrePropiedad.trim()
      });
      
      setPropiedades([...propiedades, response.data]);
      setNombrePropiedad('');
      setShowModal(false);
    } catch (error) {
      console.error('Error agregando propiedad:', error);
      alert('Error al agregar la propiedad minera');
    }
  };

  const handleCardClick = (id) => {
    if (!editMode) {
      navigate(`/propiedad/${id}`);
    }
  };

  const handleEliminar = async (e, id) => {
    e.stopPropagation();
    
    if (!window.confirm('¿Estás seguro de eliminar esta propiedad minera?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/propiedades/${id}`);
      setPropiedades(propiedades.filter(p => p.id !== id));
    } catch (error) {
      console.error('Error eliminando propiedad:', error);
      alert('Error al eliminar la propiedad minera');
    }
  };


  // Filtrar propiedades basado en el término de búsqueda
  const propiedadesFiltradas = useMemo(() => {
    if (!searchTerm.trim()) {
      return propiedades;
    }
    
    const termino = searchTerm.toLowerCase().trim();
    return propiedades.filter(propiedad => 
      propiedad.nombre.toLowerCase().includes(termino)
    );
  }, [propiedades, searchTerm]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 500, flexGrow: 1 }}>
          Propiedades Mineras
        </Typography>
        {editMode && (
          <Chip 
            label="Modo Edición" 
            color="warning" 
            size="small"
          />
        )}
      </Box>

      <TextField
        fullWidth
        label="Buscar propiedades"
        variant="outlined"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 4 }}
        placeholder="Buscar por nombre (ej: jed para encontrar 'ajedrez')"
      />

      {propiedades.length === 0 ? (
        <Box textAlign="center" py={8}>
          <Typography variant="h6" color="text.secondary">
            No hay propiedades mineras. Agrega una para comenzar.
          </Typography>
        </Box>
      ) : propiedadesFiltradas.length === 0 ? (
        <Box textAlign="center" py={8}>
          <Typography variant="h6" color="text.secondary">
            No se encontraron propiedades que coincidan con "{searchTerm}"
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {propiedadesFiltradas.map((propiedad, index) => (
            <Grid item xs={12} sm={6} md={4} key={propiedad.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: editMode ? 'default' : 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: editMode ? 'none' : 'translateY(-4px)',
                    boxShadow: editMode ? 2 : 4
                  },
                  position: 'relative',
                  border: editMode ? '2px solid' : 'none',
                  borderColor: editMode ? 'primary.main' : 'transparent'
                }}
                onClick={() => handleCardClick(propiedad.id)}
              >
                {editMode && (
                  <IconButton
                    size="medium"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      zIndex: 1,
                      backgroundColor: 'error.main',
                      color: 'white',
                      boxShadow: 2,
                      '&:hover': { 
                        backgroundColor: 'error.dark',
                        transform: 'scale(1.1)'
                      },
                      transition: 'all 0.2s'
                    }}
                    onClick={(e) => handleEliminar(e, propiedad.id)}
                    aria-label="eliminar propiedad"
                  >
                    <Delete />
                  </IconButton>
                )}
                <CardContent sx={{ flexGrow: 1, pt: 3 }}>
                  <Typography variant="h6" component="h2" gutterBottom>
                    {propiedad.nombre}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Fab
        color="primary"
        aria-label="add"
        sx={{
          position: 'fixed',
          bottom: 32,
          right: 32
        }}
        onClick={() => setShowModal(true)}
      >
        <Add />
      </Fab>

      <Dialog open={showModal} onClose={() => setShowModal(false)} maxWidth="sm" fullWidth>
        <form onSubmit={handleAgregar}>
          <DialogTitle>Agregar Propiedad Minera</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Nombre de la propiedad minera"
              type="text"
              fullWidth
              variant="outlined"
              value={nombrePropiedad}
              onChange={(e) => setNombrePropiedad(e.target.value)}
              required
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setShowModal(false);
              setNombrePropiedad('');
            }}>
              Cancelar
            </Button>
            <Button type="submit" variant="contained">
              Guardar
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
}

export default PropiedadesList;

